import React, { useEffect, useMemo, useState } from "react";
import {
    IonAccordion,
    IonButton,
    IonCol,
    IonGrid,
    IonIcon,
    IonItem,
    IonLabel,
    IonRow,
    IonSearchbar,
    IonSelect,
    IonSelectOption,
} from "@ionic/react";
import { addOutline, addSharp, pencilOutline, pencilSharp } from "ionicons/icons";
import { US_REGION_NAMES } from "../../../constants/usRegions";
import PagingButtons from "../../../components/PagingButtons";
import type {
    SettingsStateOption,
    Tier2ActiveLicenseFilter,
    Tier2AgentUiModel,
} from "../AcaSettingsTypes";

const ALL_STATES: SettingsStateOption[] = Object.entries(US_REGION_NAMES).map(([code, name]) => ({code, name,}));

interface AcaSettingsTier2AgentsSectionProps {
    data: {
        agents: Tier2AgentUiModel[];
    };
    actions: {
        openAddAgent: () => void;
        openLicenseModal: (agent: Tier2AgentUiModel) => void;
    };
}

const AcaSettingsTier2AgentsSection: React.FC<AcaSettingsTier2AgentsSectionProps> = ({data, actions,}) => {
    const { agents } = data;
    const { openAddAgent, openLicenseModal } = actions;

    const [tier2Query, setTier2Query] = useState<string>("");
    const [tier2MultiFilterStates, setTier2MultiFilterStates] = useState<string[]>([]);
    const [tier2ActiveLicenseFilter, setTier2ActiveLicenseFilter] = useState<Tier2ActiveLicenseFilter>("ANY");
    const [tier2CurrentPage, setTier2CurrentPage] = useState<number>(1);
    const [tier2PageSize, setTier2PageSize] = useState<number>(50);

    const changeTier2Page = async (page: number) => {
        setTier2CurrentPage(Number(page));
    };

    const handleTier2PageSizeChange = async (value: number) => {
        const next = Number(value) || 10;
        if (next !== tier2PageSize) {
            setTier2PageSize(next);
            setTier2CurrentPage(1);
        }
    };

    const getAgentActiveStates = (agent: Tier2AgentUiModel): string[] => {
        if (Array.isArray(agent.licenses) && agent.licenses.length) {
            return Array.from(
                new Set(
                    agent.licenses
                        .filter((license) => !!license?.active)
                        .map((license) => (license?.stateCode || "").toString().trim())
                        .filter(Boolean)
                )
            );
        }
        return (agent.stateSummary || "")
            .split(",")
            .map((stateCode) => stateCode.trim())
            .filter(Boolean);
    };

    const normTier2Query = (tier2Query || "").trim().toLowerCase();

    const filteredTier2Agents = useMemo(() => {
        return (agents || []).filter((agent) => {
            const displayName = (agent?.displayName || "").toString();
            const email = (agent?.email || "").toString();
            const npn = (agent?.npnNumber || "").toString();
            const stateSummary = (agent?.stateSummary || "").toString();

            if (normTier2Query) {
                const matches =
                    displayName.toLowerCase().includes(normTier2Query) ||
                    email.toLowerCase().includes(normTier2Query) ||
                    npn.toLowerCase().includes(normTier2Query) ||
                    stateSummary.toLowerCase().includes(normTier2Query);

                if (!matches) {
                    return false;
                }
            }

            const activeStates = getAgentActiveStates(agent);
            const hasAnyActive = activeStates.length > 0;

            if (tier2MultiFilterStates.length) {
                const matchesState = tier2MultiFilterStates.some((stateCode) => activeStates.includes(stateCode));
                if (!matchesState) {
                    return false;
                }
            }

            if (tier2ActiveLicenseFilter === "HAS_ACTIVE" && !hasAnyActive) {
                return false;
            }
            if (tier2ActiveLicenseFilter === "NO_ACTIVE" && hasAnyActive) {
                return false;
            }

            return true;
        });
    }, [agents, normTier2Query, tier2MultiFilterStates, tier2ActiveLicenseFilter]);

    const sortedTier2Agents = useMemo(
        () =>
            [...filteredTier2Agents].sort((a, b) => {
                if (a.agentActive && !b.agentActive) {
                    return -1;
                }
                if (!a.agentActive && b.agentActive) {
                    return 1;
                }
                return (a.displayName || "").localeCompare(b.displayName || "");
            }),
        [filteredTier2Agents]
    );

    const tier2TotalCount = sortedTier2Agents.length;

    const tier2NumPages = useMemo(() => {
        return Math.max(1, Math.ceil(tier2TotalCount / (Number(tier2PageSize) || 10)));
    }, [tier2TotalCount, tier2PageSize]);

    useEffect(() => {
        if (tier2CurrentPage > tier2NumPages) {
            setTier2CurrentPage(tier2NumPages);
        }
    }, [tier2CurrentPage, tier2NumPages]);

    useEffect(() => {
        setTier2CurrentPage(1);
    }, [tier2Query, tier2MultiFilterStates, tier2ActiveLicenseFilter, tier2PageSize]);

    const pagedTier2Agents = useMemo(() => {
        const size = Number(tier2PageSize) || 10;
        const page = Number(tier2CurrentPage) || 1;
        const start = (page - 1) * size;
        return sortedTier2Agents.slice(start, start + size);
    }, [sortedTier2Agents, tier2CurrentPage, tier2PageSize]);

    const renderTier2AgentTiles = (list: Tier2AgentUiModel[]) => {
        if (!list || list.length === 0) {
            return (
                <IonItem lines="none">
                    <IonLabel>No Tier 2 agents.</IonLabel>
                </IonItem>
            );
        }

        return (
            <div
                style={{
                    overflow: "auto",
                    maxHeight: "400px",
                    display: "inline-block",
                    width: "100%",
                }}
            >
                <table
                    className="aca-healthcare"
                    style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        borderSpacing: 0,
                        color: "#000",
                    }}
                >
                    <thead>
                    <tr>
                        <th style={{ textAlign: "center", width: "25%" }}>Agent Name</th>
                        <th style={{ textAlign: "center", width: "25%" }}>NPN</th>
                        <th style={{ textAlign: "center" }}>States</th>
                        <th style={{ textAlign: "center", width: "10%", whiteSpace: "nowrap" }}>
                            Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    {list.map((agent) => {
                        const isInactive = !agent.agentActive;

                        return (
                            <tr
                                key={agent.id}
                                style={isInactive ? { opacity: 0.55, filter: "grayscale(100%)" } : undefined}
                                title={isInactive ? "Inactive agent" : undefined}
                            >
                                <td style={{ textAlign: "center" }}>{agent.displayName}</td>
                                <td style={{ textAlign: "center" }}>{agent.npnNumber || "Not set"}</td>
                                <td style={{ textAlign: "center" }}>{agent.stateSummary || "None"}</td>
                                <td style={{ textAlign: "center" }}>
                                    <IonButton
                                        size="small"
                                        fill="clear"
                                        onClick={() => openLicenseModal(agent)}
                                        className="xs-button"
                                        title="Manage licenses"
                                        style={isInactive ? { opacity: 0.9 } : undefined}
                                    >
                                        <IonIcon slot="icon-only" md={pencilOutline} ios={pencilSharp} />
                                    </IonButton>
                                </td>
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <IonAccordion value="sec-tier2">
            <IonItem slot="header" color="light" lines="full">
                <IonLabel>Tier 2 Licensed Agents</IonLabel>
            </IonItem>

            <div slot="content">
                <section className="panel panel-default">
                    <IonGrid>
                        <IonRow className="ion-align-items-end">
                            <IonCol size="12" sizeMd="4">
                                <IonLabel style={{ fontSize: 14, minWidth: 90 }}>
                                    Total Agent Count: <u>{tier2TotalCount}</u>
                                </IonLabel>
                                <IonSearchbar
                                    value={tier2Query}
                                    placeholder="Search agents"
                                    onIonInput={(e) =>
                                        setTier2Query((e.detail.value ?? "").toString())
                                    }
                                    onIonClear={() => setTier2Query("")}
                                />
                            </IonCol>
                            <IonCol size="12" sizeMd="1">
                                <IonButton onClick={openAddAgent}>
                                    <IonIcon slot="icon-only" md={addOutline} ios={addSharp} />
                                </IonButton>
                            </IonCol>

                            <IonCol size="12" sizeMd="2">
                                <IonItem lines="none">
                                    <IonLabel position="stacked">Filter by states</IonLabel>
                                    <IonSelect
                                        multiple
                                        interface="popover"
                                        value={tier2MultiFilterStates}
                                        onIonChange={(e) =>
                                            setTier2MultiFilterStates(
                                                ((e.detail.value as string[]) ?? []).map((value) => value.toString())
                                            )
                                        }
                                        placeholder="Any state"
                                    >
                                        {ALL_STATES.map((state) => (
                                            <IonSelectOption key={state.code} value={state.code}>
                                                {state.code} - {state.name}
                                            </IonSelectOption>
                                        ))}
                                    </IonSelect>
                                </IonItem>
                            </IonCol>

                            <IonCol size="12" sizeMd="2">
                                <IonItem lines="none">
                                    <IonLabel position="stacked">Active state licenses</IonLabel>
                                    <IonSelect
                                        interface="popover"
                                        value={tier2ActiveLicenseFilter}
                                        onIonChange={(e) =>
                                            setTier2ActiveLicenseFilter(
                                                (e.detail.value || "ANY") as Tier2ActiveLicenseFilter
                                            )
                                        }
                                    >
                                        <IonSelectOption value="ANY">Any</IonSelectOption>
                                        <IonSelectOption value="HAS_ACTIVE">Has active license(s)</IonSelectOption>
                                        <IonSelectOption value="NO_ACTIVE">No active licenses</IonSelectOption>
                                    </IonSelect>
                                </IonItem>
                            </IonCol>

                            <IonCol
                                size="12"
                                sizeMd="2"
                                className="btn-col ion-align-self-center"
                                style={{ display: "flex", gap: 8, justifyContent: "flex-start" }}
                            >
                                <IonButton
                                    size="small"
                                    fill="outline"
                                    onClick={() => {
                                        setTier2Query("");
                                        setTier2MultiFilterStates([]);
                                        setTier2ActiveLicenseFilter("ANY");
                                    }}
                                >
                                    Clear Filters
                                </IonButton>
                            </IonCol>
                        </IonRow>
                    </IonGrid>

                    {tier2NumPages && (
                        <div style={{ float: "right", display: "inline-block" }}>
                            <PagingButtons
                                totalPages={tier2NumPages}
                                currentPage={tier2CurrentPage}
                                onPageChange={changeTier2Page}
                            />
                        </div>
                    )}
                    {tier2NumPages && (
                        <div
                            style={{
                                display: "flex",
                                gap: 12,
                                justifyContent: "flex-end",
                                alignItems: "center",
                                marginBottom: 8,
                            }}
                        >
                            <IonLabel style={{ fontSize: 12, minWidth: 90 }}>Results per page</IonLabel>
                            <IonSelect
                                value={tier2PageSize}
                                interface="popover"
                                onIonChange={(e) => handleTier2PageSizeChange(e.detail.value)}
                                style={{ minWidth: 120 }}
                            >
                                <IonSelectOption value={10}>10</IonSelectOption>
                                <IonSelectOption value={25}>25</IonSelectOption>
                                <IonSelectOption value={50}>50</IonSelectOption>
                                <IonSelectOption value={100}>100</IonSelectOption>
                            </IonSelect>
                        </div>
                    )}

                    {renderTier2AgentTiles(pagedTier2Agents)}
                    {tier2NumPages && (
                        <div style={{ float: "right", display: "inline-block" }}>
                            <PagingButtons
                                totalPages={tier2NumPages}
                                currentPage={tier2CurrentPage}
                                onPageChange={changeTier2Page}
                            />
                        </div>
                    )}
                    <div style={{ height: "60px" }}></div>
                </section>
            </div>
        </IonAccordion>
    );
};

export default AcaSettingsTier2AgentsSection;